import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UserListModal.css';

export default function FollowingModal({ userId, onClose }) {
  const [following, setFollowing] = useState([]);
  const [filter, setFilter] = useState('');
  const [unfollowingIds, setUnfollowingIds] = useState([]);
  const [total, setTotal] = useState(0);
  const debounceRef = React.useRef(null);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const skipRef = React.useRef(0);
  const requestIdRef = React.useRef(0);

  const fetchFollowing = async (s) => {
    const myRequestId = ++requestIdRef.current;
    setLoading(true);
    const token = localStorage.getItem('jwt');
    try {
      const qParam = filter ? `&q=${encodeURIComponent(filter)}` : '';
      const res = await fetch(`/user/${userId}/following?skip=${s}&limit=5${qParam}`, {
        headers: token ? { Authorization: 'Bearer ' + token } : {}
      });
      const data = await res.json();
      if (!res.ok) {
        console.warn('Failed to load following', data);
        if (s === 0) setFollowing([]);
        setTotal(0);
        setLoading(false);
        return;
      }
      const received = data.users || [];
      if (myRequestId !== requestIdRef.current) {
        setLoading(false);
        return;
      }
      if (s === 0) {
        setFollowing(received);
        setSkip(received.length);
        skipRef.current = received.length;
      } else {
        setFollowing(prev => {
          const existing = new Set(prev.map(u => u._id));
          const filtered = received.filter(u => !existing.has(u._id));
          skipRef.current += filtered.length;
          setSkip(skipRef.current);
          return [...prev, ...filtered];
        });
      }
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    // reset pagination when profile changes
    skipRef.current = 0;
    requestIdRef.current = 0;
    setFollowing([]);
    setFilter('');
    fetchFollowing(0);
  }, [userId]);

  // debounce search filter changes and fetch from server
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      requestIdRef.current = 0;
      setFollowing([]);
      fetchFollowing(0);
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [filter]);

  const handleLoadMore = () => {
    fetchFollowing(skipRef.current);
  };

  const listRef = React.useRef(null);
  const onScroll = () => {
    const el = listRef.current;
    if (!el || loading) return;
    if (following.length >= total) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
      handleLoadMore();
    }
  };

  // auto-load if container not scrollable but more items exist
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (loading) return;
    // defer check to allow DOM to update layout
    const t = setTimeout(() => {
      if (following.length < total && el.scrollHeight <= el.clientHeight) {
        handleLoadMore();
      }
    }, 50);
    return () => clearTimeout(t);
  }, [following, total, loading]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-list-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Following ({total})</h3>
          <input
            type="text"
            placeholder="Search by username"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{marginLeft:12, padding:'6px 8px', height:32}}
          />
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="user-list" ref={listRef} onScroll={onScroll} style={{overflowY:'auto', maxHeight:'60vh'}}>
          {following
            .filter(u => {
              if (!filter) return true;
              const q = filter.toLowerCase();
              return (u.userName || u.name || '').toLowerCase().includes(q);
            })
            .map(user => (
            <div key={user._id} className="user-item">
              <img src={user.Photo || '/logo192.png'} alt="" className="user-avatar" />
              <div className="user-info" onClick={() => {
                navigate(`/profile/${user._id}`);
                onClose();
              }}>
                <div className="user-name">{user.name}</div>
                <div className="user-handle">@{user.userName}</div>
              </div>
              <button
                className="remove-btn"
                disabled={unfollowingIds.includes(user._id)}
                onClick={async () => {
                  // optimistic UI: mark as unfollowing
                  setUnfollowingIds(ids => [...ids, user._id]);
                  try {
                    const token = localStorage.getItem('jwt');
                    const res = await fetch('/unfollow', {
                      method: 'PUT',
                      headers: {
                        'Content-Type': 'application/json',
                        Authorization: token ? 'Bearer ' + token : ''
                      },
                      body: JSON.stringify({ followId: user._id })
                    });
                    if (res.ok) {
                      setFollowing(prev => prev.filter(u => u._id !== user._id));
                      setTotal(t => Math.max(0, t - 1));
                    } else {
                      console.warn('Unfollow failed');
                    }
                  } catch (e) {
                    console.error(e);
                  }
                  setUnfollowingIds(ids => ids.filter(id => id !== user._id));
                }}
              >
                {unfollowingIds.includes(user._id) ? 'Unfollowing...' : 'Unfollow'}
              </button>
            </div>
          ))}
          {following.length === 0 && !loading && <p className="empty-msg">No users to show.</p>}
          {loading && (
            <div className="loading-indicator"><span className="spinner"></span>Loading...</div>
          )}
          {following.length < total && !loading && (
            <div style={{padding:'8px 16px'}}>
              <button className="load-more-btn" onClick={handleLoadMore}>Load more</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
